import * as React from 'react';
import { generateBidOptions, getNextBidMinimum, MAX_BID_LIMIT } from '@/utils/absenteeBids';
import { getBidLimitBalance, getTotalBidLimitBalance } from '@/redux/modules/bidLimit';
import { getUserData, getUserIsInGoodStanding, isUserLoggedIn } from '@/redux/modules/user';
import Select, { components } from '@liveauctioneers/caterwaul-components/lib/Select/Select';
import styled, { css, keyframes } from 'styled-components';
import ActionText from '@liveauctioneers/caterwaul-components/lib/ActionText/ActionText';
import BidLimitExceededPopover from '@/components/Popovers/BidLimitExceededPopover';
import Button from '@liveauctioneers/caterwaul-components/lib/Button/Button';
import { faLock } from '@fortawesome/pro-solid-svg-icons/faLock';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import FormattedCurrency from '@/components/Formatted/FormattedCurrency';
import { FormattedMessage } from 'react-intl';
import { getBiddingInfo } from '@/redux/modules/biddingInfo';
import { getBidIncrementsByItemId } from '@/redux/modules/bidIncrements';
import { getCatalog } from '@/redux/modules/catalog';
import { getCatalogRegistration } from '@/redux/modules/catalogRegistration';
import { getItem } from '@/redux/modules/item';
import { getLiveItemStatus } from '@/redux/modules/liveItemStatus';
import { getSeller } from '@/redux/modules/seller';
import { getSellerDetail } from '@/redux/modules/sellerDetail';
import isEqual from 'react-fast-compare';
import LogRocket from 'logrocket';
import { ModalContext } from '@/redux/modules/modal';
import noop from '@/utils/noop';
import ShouldLogInContainer from '@/components/ShouldLogIn/ShouldLogInContainer';
import Text from '@liveauctioneers/caterwaul-components/lib/Text/Text';
import usePrevious from '@/hooks/usePrevious';
import { useSelector } from 'react-redux';

type Props = {
    catalogId: number;
    itemId: number;
    openAbsenteeBidModal: Function;
    openSendSellerMessageModal: Function;
};

type SelectOption = {
    isDisabled?: boolean;
    label: any;
    value: any;
};

const ItemBiddingAbsentee = (props: Props) => {
    const { catalogId, itemId, openAbsenteeBidModal, openSendSellerMessageModal } = props;

    // items only used for logging around bidLimit bug, see:
    // https://liveauctioneers.atlassian.net/secure/RapidBoard.jspa?rapidView=43&modal=detail&selectedIssue=BUGS-3021&assignee=5f6108909109170076ef420e
    const { bidderId } = useSelector((state) => getUserData(state), isEqual);
    const totalCatalogBidLimitBalance = useSelector((state) => getTotalBidLimitBalance(state, catalogId));

    const userLoggedIn = useSelector((state) => isUserLoggedIn(state), isEqual);
    const { sellerId = 0, startPrice = 0 } = useSelector((state) => getItem(state, itemId), isEqual);
    const accountInGoodStanding = useSelector((state) => getUserIsInGoodStanding(state), isEqual);
    const bidIncrements = useSelector((state) => getBidIncrementsByItemId(state, itemId));
    const bidLimitBalance = useSelector((state) => getBidLimitBalance(state, catalogId, itemId));
    const {
        bidderHasBid,
        bidderMaxBid = 0,
        isLocked = false,
        isPassed = false,
        isSold = false,
        leadingBid = 0,
    } = useSelector((state) => getBiddingInfo(state, itemId), isEqual);
    const { bidLimit, blockedByAuctioneer, declined, suspended } = useSelector(
        (state) => getCatalogRegistration(state, catalogId),
        isEqual
    );
    const { currency = '', isCatalogOnly = false } = useSelector((state) => getCatalog(state, catalogId), isEqual);
    const { phoneNumber } = useSelector((state) => getSellerDetail(state, sellerId), isEqual);
    const { name: sellerName } = useSelector((state) => getSeller(state, sellerId));
    const { open: isLotOpen } = useSelector((state) => getLiveItemStatus(state, itemId), isEqual);

    const isUnsold = isLocked && !isPassed && !isSold && !isLotOpen;

    // need to be sure to set the bidAmount initially
    const [bidAmount, setBidAmount] = React.useState(
        getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0
    );

    const [stateItemId, setItemId] = React.useState(itemId || 0);
    const [newBidMinimum, setNewBidMinimum] = React.useState(0);
    const [overBidLimit, setOverBidLimit] = React.useState(false);
    const [shakeSelect, setShakeSelect] = React.useState(false);

    const previousBidLimitBalance = usePrevious(bidLimitBalance);
    const previousItemId = usePrevious(stateItemId);
    const previousNewBidMinimum = usePrevious(newBidMinimum);

    // If the bid (or its environment) changes, update state values
    React.useEffect(() => {
        // if there is no bid limit then `To Infinity! and Beyond!`
        const balance = bidLimitBalance || Infinity;
        // We need to store the lowest bid option so we can update the state when
        // the bid options change

        const newBidMinimum = getNextBidMinimum(bidIncrements, Math.max(bidderMaxBid, leadingBid), startPrice) || 0;

        if (
            previousBidLimitBalance !== balance ||
            previousItemId !== itemId ||
            previousNewBidMinimum !== newBidMinimum
        ) {
            const overBidLimit = newBidMinimum > balance;

            if (Boolean(overBidLimit)) {
                // add a log to document them being over bid limit
                LogRocket.captureMessage('Absentee Bid: User over bid limit', {
                    extra: {
                        bidderId,
                        bidderMaxBid,
                        bidLimitBalance: balance,
                        itemId,
                        leadingBid,
                        newBidMinimum,
                        totalCatalogBidLimitBalance,
                    },
                });
            }

            setBidAmount(newBidMinimum);
            setItemId(itemId);
            setNewBidMinimum(newBidMinimum);
            setOverBidLimit(overBidLimit);
        }
    }, [
        bidIncrements,
        bidLimitBalance,
        bidderMaxBid,
        stateItemId,
        leadingBid,
        previousBidLimitBalance,
        previousItemId,
        previousNewBidMinimum,
        startPrice,
        itemId,
        bidderId,
        totalCatalogBidLimitBalance,
    ]);

    let selectRef = React.useRef<any>();

    const onChangeBid = (selectedOption: SelectOption): void => {
        const bidAmount = parseInt(selectedOption.value, 10);
        const overBidLimit = bidAmount > bidLimitBalance;

        setBidAmount(bidAmount);
        setOverBidLimit(overBidLimit);
    };

    /**
     * Creates list of bidding options for Select dropdown
     */
    const getBidOptions = (): SelectOption[] => {
        const newBidIncrements = generateBidOptions(bidIncrements, bidAmount, newBidMinimum);

        const selectBidOptions: SelectOption[] = newBidIncrements.map((bid) => ({
            // Disable option if it exceeds user's bid limit
            isDisabled: bid > bidLimitBalance,
            label: <FormattedCurrency convert noFraction sourceCurrency={currency} value={bid} />,
            value: bid,
        }));

        return selectBidOptions;
    };

    const placeBid = (): void => openAbsenteeBidModal(stateItemId, bidAmount);

    const isStoppedFromBidding =
        !accountInGoodStanding || (userLoggedIn && (declined || blockedByAuctioneer || suspended || overBidLimit));

    const bidSelectOptions = getBidOptions();

    const modalContext: ModalContext = {
        id: stateItemId,
        type: 'place bid',
    };

    return isCatalogOnly || isPassed || isSold || isUnsold ? null : (
        <Container>
            <SelectLabelContainer>
                {bidderHasBid ? (
                    <Text colorPrimaryWeak secondary uppercase>
                        <FormattedMessage
                            id="increase_your_maximum_bid"
                            values={{
                                amount: (
                                    <StyledMaxBidFormattedCurrency sourceCurrency={currency} value={bidderMaxBid} />
                                ),
                            }}
                        />
                    </Text>
                ) : (
                    <>
                        <Text colorPrimaryWeak secondary uppercase>
                            <FormattedMessage id="select_maximum_bid" />
                        </Text>
                        <Text colorPrimaryWeak secondary uppercase>
                            <StyledLockIcon icon={faLock} />
                            &nbsp;
                            <FormattedMessage id="secure_bidding" />
                        </Text>
                    </>
                )}
            </SelectLabelContainer>
            <Select
                components={{
                    Control: (props) => (
                        <AnimatedContainer
                            onAnimationEnd={() => {
                                setShakeSelect(false);
                                setTimeout(() => selectRef.current.focus(), 0);
                            }}
                            shakeSelect={shakeSelect}
                        >
                            <components.Control {...props} />
                        </AnimatedContainer>
                    ),
                    Input: (props) => <SelectNumberInput {...props} type="number" />,
                    MenuList: (props) => (
                        <>
                            <components.MenuList {...props}>{props.children}</components.MenuList>
                            {/** Hide footer when a custom value is entered */}
                            {!props?.selectProps?.inputValue && (
                                <CustomBidSelectFooter>
                                    <Text h6 lineHeight={14} uppercase>
                                        <ActionText onClick={() => setShakeSelect(true)}>
                                            <FormattedMessage id="enterCustomBid" />
                                        </ActionText>
                                    </Text>
                                </CustomBidSelectFooter>
                            )}
                        </>
                    ),
                }}
                creatable
                createOptionPosition="first"
                disabled={overBidLimit}
                formatCreateLabel={(inputValue) => (
                    <FormattedMessage
                        id="select_create_bid_option"
                        values={{
                            amount: <FormattedCurrency convert sourceCurrency={currency} value={inputValue} />,
                        }}
                    />
                )}
                hideSpacer
                isValidNewOption={(newBidLimit) => {
                    const inputNumber = parseInt(newBidLimit, 10);
                    const isNewOption = !Boolean(bidSelectOptions.find((op) => op.value === inputNumber));
                    return (
                        Boolean(inputNumber) &&
                        isNewOption &&
                        newBidLimit >= newBidMinimum &&
                        newBidLimit <= MAX_BID_LIMIT &&
                        newBidLimit <= bidLimitBalance
                    );
                }}
                onChange={onChangeBid}
                openMenuOnFocus
                options={bidSelectOptions}
                ref={(ref) => {
                    selectRef.current = ref;
                }}
                testid="place-bid-dropdown"
                value={bidSelectOptions.find((op) => op.value === bidAmount)}
            />
            <ShouldLogInContainer
                forceSignUp={!userLoggedIn}
                modalContext={modalContext}
                requireCompleteProfile
                runFunction={isStoppedFromBidding ? noop : placeBid}
            >
                <Button disabled={isStoppedFromBidding} primary testid="itemPagePlaceBidButton">
                    <FormattedMessage id="place_bid" />
                </Button>
            </ShouldLogInContainer>
            {overBidLimit && (
                <BidLimitExceededContainer>
                    <Text colorPrimaryWeak secondary>
                        <FormattedMessage
                            id="disabled_would_exceed"
                            values={{
                                limit: <FormattedCurrency sourceCurrency={currency} value={bidLimit} />,
                            }}
                        />
                    </Text>
                    <Text colorPrimaryWeak secondary>
                        <BidLimitExceededPopover
                            id="bid-limit-exceeded"
                            openSendSellerMessageModal={openSendSellerMessageModal}
                            phoneNumber={phoneNumber}
                            sellerId={sellerId}
                            sellerName={sellerName}
                        />
                    </Text>
                </BidLimitExceededContainer>
            )}
        </Container>
    );
};

export default React.memo<Props>(ItemBiddingAbsentee, isEqual);

// Remove up/down buttons from number input
const SelectNumberInput = styled(components.Input)`
    /* Firefox */
    input[type='number'] {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -moz-appearance: textfield;
    }

    /* Chrome, Safari, Edge, Opera */
    input[type='number']::-webkit-outer-spin-button,
    input[type='number']::-webkit-inner-spin-button {
        /* stylelint-disable-next-line property-no-vendor-prefix */
        -webkit-appearance: none;
        margin: 0;
    }
`;

const CustomBidSelectFooter = styled.div`
    border-top: 1px solid ${({ theme }) => theme.colors.grey400};
    padding: 4px 20px;
    text-align: center;
    h6 {
        font-size: 12px;
        letter-spacing: 1px;
    }
    button {
        text-transform: inherit;
        :hover {
            text-decoration: underline;
        }
    }
`;

const BidLimitExceededContainer = styled.div`
    display: flex;
    flex-direction: row;
    justify-content: space-between;
    margin: 5px 0;
`;

const Container = styled.div`
    @media print {
        display: none;
    }

    > * {
        width: 100%;
    }
`;

const SelectLabelContainer = styled.div`
    margin-bottom: 4px;
    display: flex;
    justify-content: space-between;
`;

const shakeAndPulseAnimation = (theme) => keyframes`
    /** Pulse border */
    0% {
        box-shadow: 0 0 0 0 ${theme.colors.blue100};
    }
    100% {
        box-shadow: 0 0 0 6px transparent;
    }

    /** Shake */
    10%, 90% {
        transform: translate3d(-1px, 0, 0);
    }

    20%, 80% {
        transform: translate3d(2px, 0, 0);
    }

    30%, 50%, 70% {
        transform: translate3d(-4px, 0, 0);
    }

    40%, 60% {
        transform: translate3d(4px, 0, 0);
    }
`;

interface ContainerProps {
    shakeSelect: boolean;
}
const AnimatedContainer = styled.div<ContainerProps>`
    /** The Select menu z-index + 1 */
    z-index: 11;
    position: relative;
    margin-bottom: 20px;
    ${({ shakeSelect, theme }) =>
        shakeSelect &&
        css`
            animation: ${shakeAndPulseAnimation(theme)} 0.82s cubic-bezier(0.36, 0.07, 0.19, 0.97) both;
        `};
`;

const StyledLockIcon = styled(FontAwesomeIcon)`
    height: 14px;
`;

const StyledMaxBidFormattedCurrency = styled(FormattedCurrency)`
    display: contents;
`;
